import { Checkbox, Form, Select } from "antd";
import React from "react";
import { filterOptionByName } from "src/common/filterOptionByName";
import { SelectFromReference } from "src/components/SelectFromReference";
import { edAttrField } from "src/pages/ManagementPage/Obj2Tab/EdAttribute";
import { loadFromAttr } from "src/pages/ManagementPage/objectsApi";
import { AttrTypeName } from "src/types/AttrType";
import { ZAttribute } from "src/types/ZAttribute";
import { childEntsButtonOptions } from "../../childEntsButtonOptions";
import { loadAllObjectAttsOptions } from "../../loadAllObjectAttsOptions";
import { edCompField } from "../ParamsEditorInfo2";

interface Props {
  attrTypesDict: Record<number, string>;
}

export const ChildEntitiesEdProps: React.FC<Props> = ({ attrTypesDict }) => {
  const refId = Form.useWatch(edAttrField.referenceId);
  const childDictionaryAttributeId = Form.useWatch(
    edAttrField.childDictionaryAttributeId,
  );
  const form = Form.useFormInstance();
  const previousRefId = React.useRef(refId);
  const isCalculableAttr = (attr: ZAttribute) =>
    attrTypesDict[attr.valueType] === AttrTypeName.int ||
    attrTypesDict[attr.valueType] === AttrTypeName.number ||
    attrTypesDict[attr.valueType] === AttrTypeName.formula;
  const loadChildDictionaryAttributes = React.useCallback(
    () =>
      loadAllObjectAttsOptions(
        refId,
        (attr) => attrTypesDict[attr.valueType] === AttrTypeName.dictSingle,
      ),
    [attrTypesDict, refId],
  );

  React.useEffect(() => {
    if (
      previousRefId.current !== undefined &&
      previousRefId.current !== refId
    ) {
      form.setFieldsValue({
        [edAttrField.childDictionaryAttributeId]: null,
        [edAttrField.childDictionaryValueId]: null,
      });
    }
    previousRefId.current = refId;
  }, [form, refId]);

  const loadChildDictionaryValues = React.useCallback(async () => {
    if (!childDictionaryAttributeId) return [];
    const values = await loadFromAttr(childDictionaryAttributeId);
    return values.map(({ id, name }) => ({
      value: String(id),
      label: name,
    }));
  }, [childDictionaryAttributeId]);

  return (
    <>
      <Form.Item
        label="Связанный атрибут"
        name={edCompField("attrId")}
        rules={[{ required: true }]}
      >
        <SelectFromReference
          loader={() =>
            loadAllObjectAttsOptions(
              refId,
              (attr) => attrTypesDict[attr.valueType] === AttrTypeName.object,
            )
          }
        />
      </Form.Item>
      <Form.Item
        label="Атрибут ограничения списка"
        name={edAttrField.childDictionaryAttributeId}
      >
        <SelectFromReference
          allowClear
          showSearch
          filterOption={filterOptionByName}
          loader={loadChildDictionaryAttributes}
          onChange={() =>
            form.setFieldValue(edAttrField.childDictionaryValueId, null)
          }
        />
      </Form.Item>
      <Form.Item
        label="Значение атрибута ограничения списка"
        name={edAttrField.childDictionaryValueId}
        rules={[
          {
            required: Boolean(childDictionaryAttributeId),
            message:
              "Пожалуйста, введите Значение атрибута ограничения списка.",
          },
        ]}
      >
        <SelectFromReference
          allowClear
          showSearch
          disabled={!childDictionaryAttributeId}
          filterOption={filterOptionByName}
          loader={loadChildDictionaryValues}
        />
      </Form.Item>
      <Form.Item name={edCompField("editable")} valuePropName="checked" noStyle>
        <Checkbox>Редактировать атрибут в модальном окне</Checkbox>
      </Form.Item>
      <>
        <Form.Item
          label="Суммировать значения атрибутов"
          name={edCompField("summaryAttrs")}
          tooltip="Суммировать числовые значения атрибутов зависимых объектов"
        >
          <SelectFromReference
            loader={() => loadAllObjectAttsOptions(refId, isCalculableAttr)}
            mode="multiple"
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item label="Отображение кнопок" name={edCompField("buttons")}>
          <Select
            mode="multiple"
            maxTagCount="responsive"
            options={childEntsButtonOptions}
          />
        </Form.Item>
      </>
    </>
  );
};
